import { useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck, Target, Calculator, FileText, Sparkles, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { Seo, SITE_URL } from "@/components/Seo";
import { IdeaEntry } from "@/components/IdeaEntry";
import { getConfig } from "@/lib/api";
import { branchen } from "@/content/branchen";

const SCHMERZ_ICONS = [Calculator, Target, AlertTriangle];
const LOESUNG_ICONS = [Calculator, ShieldCheck, FileText, Sparkles];

const DISCLAIMER =
  "Hinweis: Die KI-gestützte Analyse dient als automatisierte Orientierungshilfe und ordnet die Risiko-Faktoren deiner Idee qualitativ ein. Sie berechnet keine exakten Finanzkennzahlen (z. B. Marge, Kundengewinnungskosten), stellt keine Rechts-, Steuer- oder Unternehmensberatung dar und garantiert keinen wirtschaftlichen Erfolg.";

const scrollToStart = () => {
  document.getElementById("branche-start")?.scrollIntoView({ behavior: "smooth", block: "center" });
};

export default function BranchePage() {
  const { slug } = useParams();
  const content = branchen[slug];
  const [founders, setFounders] = useState(null);

  useEffect(() => {
    getConfig().then((c) => setFounders({ limit: c.founder_limit, remaining: c.founders_remaining })).catch(() => {});
  }, []);

  if (!content) return <Navigate to="/" replace />;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: content.breadcrumb, item: `${SITE_URL}${content.path}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen" data-testid={`branche-page-${slug}`}>
      <Seo title={content.seo.title} description={content.seo.description} path={content.path} keywords={content.seo.keywords} image={content.ogImage} type="article" noindex jsonLd={jsonLd} />
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-16 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200/80 text-xs uppercase tracking-widest text-slate-600 mb-4 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full brand-gradient-bg" />
              {content.eyebrow}
            </div>

            {founders && founders.remaining > 0 && (
              <div className="block sm:inline-flex items-center gap-2 mb-6 sm:ml-3 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800 w-fit">
                <Flame className="h-3.5 w-3.5 text-amber-500 inline" /> Einführungspreis 29 € statt 49 € · Limitiert auf die ersten {founders.limit} Gründer · noch {founders.remaining} frei
              </div>
            )}

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
              {content.hero.h1}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">{content.hero.sub}</p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button data-testid="branche-hero-cta" onClick={scrollToStart} className="h-13 px-7 py-6 rounded-2xl font-semibold text-white brand-gradient-bg text-base">
                Idee jetzt testen – Ersteinschätzung gratis, Report 29 €
              </Button>
            </div>
            <p className="mt-3 text-sm text-slate-500 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" style={{ color: "#10C98D" }} /> Ersteinschätzung gratis · anonym & datensparsam
            </p>
          </div>

          <div className="lg:col-span-5 w-full animate-fade-in-up" style={{ animationDelay: "120ms" }}>
            <IdeaEntry />
          </div>
        </div>
      </section>

      {/* SCHMERZ */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>{content.schmerz.title}</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">{content.schmerz.intro}</p>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {content.schmerz.items.map((it, i) => {
            const Icon = SCHMERZ_ICONS[i % SCHMERZ_ICONS.length];
            return (
              <div key={i} data-testid={`branche-schmerz-${i}`} className="bg-white rounded-2xl border border-slate-200/70 p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="h-11 w-11 rounded-xl bg-rose-50 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-rose-500" />
                </div>
                <div className="font-semibold text-slate-900">{it.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* REALITY-CHECK */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-300 mb-4">
              <AlertTriangle className="h-4 w-4" /> Reality-Check
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>{content.reality.title}</h2>
            {content.reality.body.map((p, i) => (
              <p key={i} className="mt-4 text-slate-300 leading-relaxed text-[15px]">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* LÖSUNG */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>{content.loesung.title}</h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {content.loesung.features.map((ft, i) => {
            const Icon = LOESUNG_ICONS[i % LOESUNG_ICONS.length];
            return (
              <div key={i} data-testid={`branche-loesung-${i}`} className="flex items-start gap-4 bg-white rounded-2xl border border-slate-200/70 p-6 shadow-sm">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Icon className="h-5 w-5" style={{ color: "#10C98D" }} />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{ft.title}</div>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{ft.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section id="branche-start" className="max-w-3xl mx-auto px-6 pb-20 scroll-mt-24">
        <div className="bg-white rounded-3xl border border-slate-200/70 shadow-xl p-6 sm:p-10 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>{content.ctaTitle}</h2>
          <p className="mt-3 text-slate-600">{content.ctaSub}</p>
          <div className="mt-8 text-left">
            <IdeaEntry />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <p className="text-xs text-slate-500 leading-relaxed max-w-3xl" data-testid="branche-disclaimer">{DISCLAIMER}</p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
            <a href="/impressum" className="hover:text-slate-900">Impressum</a>
            <a href="/datenschutz" className="hover:text-slate-900">Datenschutz</a>
            <a href="/agb" className="hover:text-slate-900">AGB</a>
            <a href="/widerruf" className="hover:text-slate-900">Widerruf</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
